import React, { useCallback, useContext, useMemo } from 'react';
import EDDialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import { FranvaroContext } from '../../Context/FranvaroContext/FranvaroContext';
import BusinessDataFormContext from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
// import { ModelContext } from '../../../../../../../../Model/ModelContext/ModelContext';
import clientAvenyModel from '../../../../../../../Model/clientAvenyModel';
import { IClientBusinessModel } from '../../../../../../../Types/ClientModelTypes';
import { EDFullscreenPaper } from '../../../../../../../BaseComponents/FullscreenModal/Components/EDFullscreenPaper';
import ObjektVy from '../../../../../_Common/Components/BusinessCollectionComponents/ObjektVy/ObjektVy';
import ObjektVySida from '../../../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import ObjektVyKolumner, {
  ObjektVyKolumn,
} from '../../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKolumner/ObjektVyKolumner';
import { ObjektVyKort } from '../../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import { FranvaroView } from '../FranvaroView/FranvaroView';
import { makeStyles } from 'tss-react/mui';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import useNodeQuery from '../../../../../../../Components/QueryComponents/NodeQuery/useNodeQuery';
import { selectedId } from '../../../../../../../Components/MasterDetail/MasterDetailUtils';
import {
  UserQuery,
  UserQueryVariables,
  UserQuery_node_TEDSYSUser,
} from '../../Queries/_QueryTypes/UserQuery';
import query from '../../Queries/UserQuery.gql';
import AddFranvaroButton from '../AddFranvaroButton/AddFranvaroButton';
import { useCollectionMutation } from '../../../../../../../Components/QueryComponents/CollectionMutation/useCollectionMutation';
import {
  UserUpdateMutation,
  UserUpdateMutationVariables,
} from '../../Mutations/_QueryTypes/UserUpdateMutation';
import updateMutation from '../../Mutations/UserUpdateMutation.gql';
import {
  IFormData,
  removeIdsFromNewAssociations,
} from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { FranvaroReservForView } from '../FranvaroReservFor/FranvaroReservForView';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';

const userModel: IClientBusinessModel =
  clientAvenyModel.SYS.BusinessModels.SYSUser;

interface IProps {
  routineNumber: number;
}

const useStyles = makeStyles()(theme => ({
  container: {
    alignItems: 'flex-start',
    display: 'flex',
    flexFlow: 'column nowrap',
  },
  header: {
    backgroundColor: theme.palette.primary.dark,
  },
  contentWrapper: { margin: 0, height: '100%' },
}));

const FranvaroDialog: React.FC<IProps> = props => {
  const { classes } = useStyles();
  const { activeDatabase, userID } = useContext(ActiveDatabaseContext);
  const franvaroContext = useContext(FranvaroContext);
  const franvaroContextSetDialogOpen = franvaroContext.setFranvaroDialogOpen;
  const closeDialog = () => {
    franvaroContextSetDialogOpen(false);
  };

  const {
    nodeData,
    loading: nodeLoading,
    refetch,
  } = useNodeQuery<UserQuery, UserQuery_node_TEDSYSUser, UserQueryVariables>(
    query,
    selectedId(userID),
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: !userID && !franvaroContext.franvaroDialogOpen,
      variables: {
        id: userID ?? '',
        toDate: new Date().toLocaleDateString('sv-SE'),
      },
    }
  );

  const [mutation, { loading: mutationLoading, error }] = useCollectionMutation<
    UserUpdateMutation,
    UserUpdateMutationVariables
  >(updateMutation);

  const updateFunction = useCallback(
    (values: IFormData) => {
      const newValues = removeIdsFromNewAssociations(values ?? {});
      return mutation({
        variables: {
          dbName: activeDatabase || '',
          values: {
            id: nodeData?.id ?? '',
            _version: nodeData?._version ?? 1,
            ...newValues,
          },
          routine: props.routineNumber,
          toDate: new Date().toLocaleDateString('sv-SE'),
        },
      });
    },
    [
      mutation,
      activeDatabase,
      nodeData?.id,
      nodeData?._version,
      props.routineNumber,
    ]
  );

  const franvaroVyer = useMemo(() => {
    return [FranvaroView];
  }, []);

  const franvaroReserForVyer = useMemo(() => {
    return [FranvaroReservForView];
  }, []);

  return (
    <BusinessDataFormContext<UserQuery_node_TEDSYSUser>
      data={nodeData}
      dataLoading={nodeLoading}
      fullScreen={false}
      isNew={false}
      model={userModel}
      mutationError={error}
      mutationLoading={mutationLoading}
      updateMutation={updateFunction}
      refetchData={refetch}
    >
      <EDDialog
        classes={{ contentWrapper: classes.contentWrapper }}
        fullHeight
        fullWidth
        maxWidth={false}
        open={franvaroContext.franvaroDialogOpen}
        onEntered={() => {
          refetch();
          franvaroContext.franvaroReservForRefetch();
        }}
        footerActions={[
          {
            label: 'Stäng',
            onClick: () => {
              closeDialog();
            },
          },
        ]}
        onClose={closeDialog}
        title="Frånvaro"
      >
        <EDFullscreenPaper fullHeight>
          <ObjektVy nodeLoading={nodeLoading} sidaVidSida={false} hideEditMenu>
            <ObjektVySida
              caption="Frånvaro"
              sidNr={1}
              collectionName="SYSUser"
              smallScreenNoPadding
            >
              {nodeData?.isAktivAttestant && (
                <ObjektVyKort noMargin>
                  <EDTypography>
                    Glöm ej att även registrera frånvaro i rutin Fakturaattest
                  </EDTypography>
                </ObjektVyKort>
              )}
              <ObjektVyKolumner>
                <ObjektVyKolumn fullPage>
                  <ObjektVyKort
                    bmod={userModel}
                    button={() => (
                      <AddFranvaroButton
                        setEditFranvaro={franvaroContext.setEditFranvaro}
                        setDialogOpen={
                          franvaroContext.setRegisterFranvaroDialogOpen
                        }
                      />
                    )}
                    caption="Frånvaro"
                    id={userID ?? ''}
                    vyer={franvaroVyer}
                  />
                </ObjektVyKolumn>
                <ObjektVyKolumn fullPage>
                  <ObjektVyKort
                    readOnly
                    bmod={userModel}
                    caption="Reserv för"
                    id={userID ?? ''}
                    vyer={franvaroReserForVyer}
                  />
                </ObjektVyKolumn>
              </ObjektVyKolumner>
            </ObjektVySida>
          </ObjektVy>
          {nodeLoading || (mutationLoading && <EDAvenyProgress />)}
        </EDFullscreenPaper>
      </EDDialog>
    </BusinessDataFormContext>
  );
};

export default FranvaroDialog;
